import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { submitTramite } from '../services/submitTramiteService';

const HARDCODING_PEDIDO_MEDICO = {
  idDocumentacion: 954,
  documentacion: 'Pedido Médico',
  descripcion: 'Documento requerido para la autorización de cobertura',
  requerido: false,
  activo: true,
};

const HARDCODING_ESTUDIOS_MEDICOS = {
  idDocumentacion: 955,
  documentacion: 'Estudios Previos',
  descripcion: 'Documentos requeridos para la autorización de cobertura',
  requerido: false,
  activo: true,
};

const useUploadTramitesSAT = ({ autorizacion, setMessage }) => {
  const [tramiteData, setTramiteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // { [idDocumentacion]: File[] }
  const [documentacionFiles, setDocumentacionFiles] = useState({});

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      afiliado: '',
      tipoTramite: '',
      solicitudCobertura: '',
      observacion: '',
    },
  });

  useEffect(() => {
    if (!autorizacion) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = autorizacion || {};

      setTramiteData({
        ...data,
        documentacionTipoCobertura: [HARDCODING_PEDIDO_MEDICO, HARDCODING_ESTUDIOS_MEDICOS],
      });

      if (data.numerodocumento && data.afiliado) {
        setValue('afiliado', `${data.numerodocumento} - ${data.afiliado}`);
      }
      if (data.tipoCobertura) setValue('tipoTramite', data.tipoCobertura);
      if (data.idSolicitudCobertura) setValue('solicitudCobertura', data.idSolicitudCobertura);

      setIsLoading(false);
    } catch (err) {
      setError('No se pudieron cargar los datos del trámite.');
      setIsLoading(false);
      setMessage({ open: true, message: `Error al cargar los datos del trámite: ${err.message}`, severity: 'error' });
    }
  }, [autorizacion, setValue, setMessage]);

  const setFiles = useCallback((idDocumentacion, files) => {
    setDocumentacionFiles((prev) => ({ ...prev, [idDocumentacion]: files }));
  }, []);

  const hasFiles = Object.values(documentacionFiles).some((files) => files.length > 0);

  const canSubmit = useCallback(() => {
    if (!hasFiles) {
      return { can: false, reason: 'Debe adjuntar al menos un archivo para enviar el trámite' };
    }
    return { can: true, reason: null };
  }, [hasFiles]);

  const onSubmit = useCallback(
    async (formData) => {
      const submitValidation = canSubmit();
      if (!submitValidation.can) {
        setMessage({ open: true, message: submitValidation.reason, severity: 'error' });
        return;
      }

      setIsSubmitting(true);

      try {
        const documentacionTipoCobertura = tramiteData.documentacionTipoCobertura
          .filter((doc) => doc.activo)
          .map((doc) => ({
            idDocumentacion: doc.idDocumentacion,
            documentacion: doc.documentacion,
            requerido: doc.requerido,
            files: (documentacionFiles[doc.idDocumentacion] || []).map((f, i) => ({
              fileName: f.name,
              fileSize: f.size,
              fileType: f.type,
              fileKey: `files_${doc.idDocumentacion}_${i}`,
            })),
          }))
          .filter((doc) => doc.files.length > 0);

        const payload = new FormData();
        payload.append('afiliado', tramiteData.afiliado || '');
        payload.append('tipoCobertura', tramiteData.tipoCobertura || '');
        payload.append('idSolicitudCobertura', tramiteData.idSolicitudCobertura || '');
        payload.append('observacion', formData.observacion || '');
        payload.append('documentacionTipoCobertura', JSON.stringify(documentacionTipoCobertura));

        documentacionTipoCobertura.forEach((doc) => {
          (documentacionFiles[doc.idDocumentacion] || []).forEach((_, i) => {
            const file = documentacionFiles[doc.idDocumentacion][i];
            payload.append(`files_${doc.idDocumentacion}_${i}`, file, file.name);
          });
        });

        const response = await submitTramite(payload);

        setMessage({ open: true, message: response.message || 'Trámite enviado correctamente', severity: 'success' });
        setIsSuccess(true);
      } catch (err) {
        setMessage({ open: true, message: `Error al enviar el trámite: ${err.message}`, severity: 'error' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [tramiteData, documentacionFiles, canSubmit, setMessage],
  );

  return {
    tramiteData,
    isLoading,
    error,
    documentacionFiles,
    isSubmitting,
    isSuccess,
    control,
    handleSubmit,
    errors,
    setFiles,
    onSubmit,
    canSubmit,
  };
};

export default useUploadTramitesSAT;
